/**
 * الصفحةُ التي لا تُحمَّل (29 سبتمبر 2026) — شكوى صاحب المنصة: «أدخل صفحةً فتبقى على لون الخلفية
 * ولا أقدر أرجع ولا أحدّث». سببُها الأول **نشرٌ جديد وتبويبٌ قديم**: الشاشاتُ تُحمَّل عند الحاجة
 * (`lazy`) بأسماءٍ مبصومة، وبعد كل نشرٍ تتغيّر البصماتُ وتزول القديمة من الخادم — فالتبويبُ الذي فُتح
 * قبل النشر يطلب عند أول تنقّلٍ ملفّاً لم يعد موجوداً، فيرمي `import()` خطأً، وبلا حدودِ خطأ يُفرغ
 * React الشجرةَ كلَّها: خلفيةٌ بلا شيء، وفي التطبيق المثبَّت لا زرَّ تحديثٍ ولا رجوع.
 *
 * العلاجُ ثلاث طبقات:
 *   ١) `vite:preloadError`: فيتي يطلقه حين يفشل تحميلُ شاشة — نعيد تحميل الصفحة **مرّةً** (الصفحةُ
 *      الجديدة تجلب `index.html` بلا تخزين فتعرف البصماتِ الجديدة). ومرّةٌ واحدة كي لا ندور إن كان
 *      العطلُ في الشبكة لا في النشر.
 *   ٢) حدودُ خطأ حول الشاشات (`AppErrorBoundary`): ما لم تنجح الإعادة يرى المستخدمُ بطاقةً بأزرارٍ
 *      (تحديث · رجوع · الرئيسية) لا فراغاً.
 *   ٣) شريطُ «صدر تحديث»: حين يتولّى عاملُ خدمةٍ جديد التبويبَ — أي نُشرت نسخةٌ — يُدعى المستخدمُ
 *      إلى التحديث قبل أن يصطدم بشاشةٍ لا تُحمَّل.
 */
const RELOAD_KEY = 'rafrafa.recoveryReloadAt';
/** إعادةُ تحميلٍ واحدة كل نصف دقيقة — أكثرُ من ذلك دورانٌ لا علاج */
const RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Loading (CSS )?chunk|preload CSS|Failed to fetch|NetworkError|Load failed/i;

/** أخطأ فشلُ تحميل شاشة (ملفٌّ مبصوم لم يعد موجوداً أو انقطعت الشبكة أثناء جلبه)؟ */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : '';
  return CHUNK_ERROR.test(msg);
}

/** آخرُ إعادةِ تحميلٍ للتعافي — تُقرأ من تخزين التبويب (تُمحى بإغلاقه) */
function lastRecoveryReload(now = Date.now()): number {
  try {
    const v = Number(sessionStorage.getItem(RELOAD_KEY));
    return v > 0 && now - v < RELOAD_WINDOW_MS ? v : 0;
  } catch { return 0; }
}

/** هل يجوز أن نعيد التحميل الآن (لم نفعلها خلال النافذة)؟ — حسابٌ صرف للاختبار */
export function mayRecoverByReload(now = Date.now()): boolean {
  return lastRecoveryReload(now) === 0;
}

/**
 * إعادةُ تحميل الصفحة مرّةً للتعافي. تعيد `true` إن أُطلقت، و`false` إن سبقت إعادةٌ في النافذة
 * (فيُترك الأمرُ لحدود الخطأ تعرض بطاقتَها).
 */
export function recoverByReload(reason: string): boolean {
  if (!mayRecoverByReload()) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* تخزينٌ معطّل: نعيد مرّةً بلا حارس */ }
  if (typeof console !== 'undefined') console.warn(`[rafrafa] إعادةُ تحميلٍ للتعافي: ${reason}`);
  window.location.reload();
  return true;
}

let installed = false;
/** يُنادى مرّةً عند الإقلاع: فشلُ تحميل شاشةٍ ← إعادةُ تحميلٍ واحدة */
export function installPreloadRecovery(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('vite:preloadError', (event) => {
    const e = event as Event & { payload?: unknown };
    if (recoverByReload(`vite:preloadError ${e.payload instanceof Error ? e.payload.message : ''}`)) event.preventDefault();
  });
}

type Listener = (v: boolean) => void;
const listeners = new Set<Listener>();
let updateReady = false;

/** هل تولّى عاملُ خدمةٍ جديد هذا التبويب (نُشرت نسخةٌ أحدثُ مما يعمل هنا)؟ */
export function isUpdateReady(): boolean { return updateReady; }
export function onUpdateReady(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; }
function announce(): void { updateReady = true; for (const l of listeners) l(true); }

/**
 * تسجيلُ عامل الخدمة ومراقبةُ تبدّله: `controllerchange` يقع حين يتولّى عاملٌ جديد التبويبَ —
 * وبما أن `sw.js` يطلب `skipWaiting` و`clients.claim` فهو يقع عند أول تحميلٍ بعد كل نشر. أولُ
 * تثبيتٍ (لا متحكّمَ قبله) ليس تحديثاً. وعند عودة التبويب إلى الواجهة يُسأل الخادمُ عن نسخةٍ أحدث
 * (المتصفّح يسأل وحده كل يوم فقط).
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => { if (hadController) announce(); });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      let last = Date.now();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60_000) return;
        last = Date.now();
        void reg.update().catch(() => undefined);
      });
    }).catch(() => { /* المتصفّح رفض — تعمل المنصة كموقعٍ عادي */ });
  });
}
